export type CompressFormat = "jpeg" | "png";
export type OutputType = "base64" | "blob";

/**
 * Carrega um BLOB e retorna um DataURL.
 * @param file
 */
export function convertUrlToBase64(url: string): Promise<ConvertUrlToBase64Result> {
    return new Promise<ConvertUrlToBase64Result>((resolve, reject) => {

        var img = new Image();
        img.onload = () => {
            var canvas = document.createElement("canvas");
            var context = canvas.getContext('2d');
            canvas.width = img.width;
            canvas.height = img.height;
            context.drawImage(img, 0, 0);

            resolve({
                width: img.width,
                height: img.height,
                dataUrl: canvas.toDataURL(null, 1)
            });
        };
        img.onerror = () => {
            reject("Load Error");
        }
        img.src = url;
    });
}

/**
 * Converte um BLOB em Base64.
 * @param file
 */
export function convertBlobToBase64(file: Blob): Promise<string> {
    return new Promise<string>((resolve, error) => {
        const reader = new FileReader();
        if (file) {
            reader.readAsDataURL(file);
            reader.addEventListener("load", () =>
                resolve(reader.result as string)
            );
            reader.onerror = (err: any) => {
                error(err);
            };
        }
    });
}

/**
 * Converte um BLOB URL em uma Base64.
 * @param blobUrl
 */
export function convertBlobUrlToBase64(blobUrl: string): Promise<string> {

    return new Promise((resolve) => {

        var xhr = new XMLHttpRequest();
        xhr.responseType = 'blob';
        xhr.onload = () => {
            var recoveredBlob = xhr.response;
            var reader = new FileReader();
            reader.onload = () => {
                resolve(reader.result as string);
            };
            reader.readAsDataURL(recoveredBlob);
        };
        xhr.open('GET', blobUrl);
        xhr.send();
    });
}

/**
 * Converte Base64 em um objeto FILE.
 * @param dataUrl
 * @param contentType
 */
export function convertBase64ToFile(dataUrl: string, name: string = "file", contentType: string = "image/jpeg"): File {
    return new File(convertBase64ToArray(dataUrl), name, { type: contentType });
}

/**
 * Converte Base64 em um objeto BLOB.
 * @param dataUrl
 * @param contentType
 */
export function convertBase64lToBlob(dataUrl: string, contentType: string = "image/jpeg"): Blob {
    return new Blob(convertBase64ToArray(dataUrl), { type: contentType });
}

/**
 * Converte um DataURL para um elemento do tipo Imagem.
 * @param urlOrDataUrl
 */
export function convertUrlToImage(urlOrDataUrl: string): Promise<HTMLImageElement> {

    return new Promise((resolve, error) => {

        var img = new Image();
        img.setAttribute('crossOrigin', 'anonymous');
        img.onload = () => {
            resolve(img);
        };
        img.onerror = (e: any) => {
            console.log(e);
        };
        img.src = urlOrDataUrl;
    });
}


/**
 * Converte um elemento CANVAS em BlobURL.
 * Este método usa URL.createObjectURL.
 * @param canvas
 * @param contentType
 * @param dataUrlToRevoke
 */
export function convertCanvasToBlobUrl(canvas: HTMLCanvasElement, contentType: string = "image/jpeg"): Promise<string> {

    return new Promise((resolve, reject) => {

        canvas.toBlob(
            (blob: Blob) => {
                if (!blob) {
                    reject(new Error('Canvas is empty'));
                }
                else {
                    resolve(window.URL.createObjectURL(blob));
                }
            }, contentType);
    });
}

/**
 * Converte uma Imagem em array de bytes.
 * @param img
 */
export function convertImageToArray(img: HTMLImageElement): Array<Uint8Array> {
    // Create an empty canvas element
    var canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;

    // Copy the image contents to the canvas
    var ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);

    // Get the data-URL formatted image
    // Firefox supports PNG and JPEG. You could check img.src to
    // guess the original format, but be aware the using "image/jpg"
    // will re-encode the image.
    var dataURL = canvas.toDataURL("image/png", 1);

    return convertBase64ToArray(dataURL);
}

/**
 * Converte Base64 em array de bytes.
 * @param dataUrl
 */
export function convertBase64ToArray(dataUrl: string): Array<Uint8Array> {

    // convert base64/URLEncoded data component to raw binary data held in a string
    var byteString;
    if (dataUrl.split(',')[0].indexOf('base64') >= 0)
        byteString = atob(dataUrl.split(',')[1]);
    else
        byteString = unescape(dataUrl.split(',')[1]);

    // write the bytes of the string to a typed array
    var ia = new Uint8Array(byteString.length);
    for (var i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }

    return [ia];
}

/**
 * Libera um objeto URL que foi criado préviamente chamando URL.createObjectURL()
 * @param dataUrl
 */
export function revokeObjectUrlIfBlob(dataUrl: string): void {

    if (dataUrl && dataUrl.indexOf("blob") == 0) {
        window.URL.revokeObjectURL(dataUrl);
    }
}

/**
 * Obtém um elemento FormData para upload do Blob como um arquivo.
 * @param blob
 * @param filename
 */
export function getBlobFormData(blob: Blob, filename: string): FormData {
    var fd = new FormData(document.forms[0]);
    fd.append('file', blob, filename);
    return fd;
}

/**
 * Redimensionamento de imagens.
 */
export class Resizer {

    private static changeHeightWidth(height: number, width: number, maxHeight: number, maxWidth: number) {
        // Redimensiona a primeira vez mantendo a proporcionalidade.
        if (width > height && width > maxWidth) {
            height = Math.round(height * maxWidth / width);
            width = maxWidth;
        }
        else if (height > width) {
            width = Math.round(width * maxHeight / height);
            height = maxHeight;
        }

        // Verificar se os limites especificados foram estrapolados.
        if (height > maxHeight) {
            width = Math.round(width * maxHeight / height);
            height = maxHeight;
        }
        else if (width > maxWidth) {
            height = Math.round(height * maxWidth / width);
            width = maxWidth;
        }

        return { height, width };
    }

    private static resizeAndRotateImage(image: HTMLImageElement, maxHeight: number, maxWidth: number, compressFormat: CompressFormat = "jpeg", quality: number = 100, rotation = 0): ConvertUrlToBase64Result {
        const qualityDecimal = quality / 100;
        const canvas = document.createElement('canvas');

        let width = image.naturalWidth;
        let height = image.naturalHeight;

        var newHeightWidth = this.changeHeightWidth(height, width, maxHeight, maxWidth);
        if (rotation && (rotation === 90 || rotation === 270)) {
            canvas.width = newHeightWidth.height;
            canvas.height = newHeightWidth.width;
        } else {
            canvas.width = newHeightWidth.width;
            canvas.height = newHeightWidth.height;
        }

        width = newHeightWidth.width;
        height = newHeightWidth.height;

        var ctx = canvas.getContext("2d");

        if (rotation) {
            ctx.rotate(rotation * Math.PI / 180);
            if (rotation === 90) {
                ctx.translate(0, -canvas.width);
            } else if (rotation === 180) {
                ctx.translate(-canvas.width, -canvas.height);
            } else if (rotation === 270) {
                ctx.translate(-canvas.height, 0);
            } else if (rotation === 0 || rotation === 360) {
                ctx.translate(0, 0);
            }
        }
        ctx.drawImage(image, 0, 0, width, height);

        return {
            dataUrl: canvas.toDataURL(`image/${compressFormat}`, qualityDecimal),
            height: height,
            width: width
        };
    }


    public createResizedImage(
        image: HTMLImageElement,
        maxWidth: number,
        maxHeight: number,
        compressFormat: CompressFormat,
        quality: number,
        rotation: number,
        onComplete: (data: ConvertUrlToBase64Result) => void,
    ) {
        if (image) {
            var resizedDataUrl = Resizer.resizeAndRotateImage(image, maxHeight, maxWidth, compressFormat, quality, rotation);
            onComplete(resizedDataUrl);
        }
    }

}
